import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { serverAddress } from "../../config";

const UI_ONLY_ACTIONS = new Set(["", "CANCEL", "CLOSE"]);
const CHECKER_TYPES = new Set(["APPROVE", "REJECT"]);

const normalizeAction = value => String(value || "").trim().toUpperCase();

export const resolveButtonAuthorization = item => {
  const action = normalizeAction(item?.component?.options?.action);
  const type = normalizeAction(item?.component?.options?.type);

  return {
    action,
    type,
    requiresChecker: CHECKER_TYPES.has(type)
  };
};

const getVerifyTemplate = item =>
  item?.data?.sWorkflowVerify ||
  item?.data?.sActionVerify ||
  item?.data?.sVerify ||
  item?.component?.options?.workflowVerify ||
  item?.component?.options?.sWorkflowVerify ||
  item?.component?.options?.sVerify ||
  "";

export const resolveButtonVerifyUrl = (template, values = {}) =>
  String(template || "").replace(/\[([^\]]+)]|\{([^{}]+)}/g, (match, squareKey, braceKey) => {
    const key = String(squareKey || braceKey || "").trim();
    const value = values?.[key];

    return value === undefined || value === null || value === ""
      ? match
      : encodeURIComponent(String(value));
  });

const accessSignature = ({
  moduleName,
  action,
  type,
  verifyUrl,
  checkerUser,
  formType
}) =>
  [moduleName, action, type, verifyUrl, checkerUser, formType].join("::");

const responseAllowsAccess = response =>
  String(response?.data?.data?.Access || "").trim().toUpperCase() === "YES";

/**
 * Shared fail-closed action authorization for BUTTON, BUTTONDROPDOWN and
 * BUTTONLISTITEM. Each distinct action/verification combination is evaluated
 * independently and duplicate combinations share the same request.
 */
export default function useButtonActionAccess({
  items = [],
  formName,
  formData,
  formValues,
  token
}) {
  const moduleName = formName || formData?.form?.sFormName || "";
  const formType = formData?.form?.sFormType || "";
  const checkerUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("globalvariables"))?.UserCode || "";
    } catch (error) {
      return "";
    }
  }, []);

  const values = useMemo(
    () => ({ ...(formData?.data?.mainrecord || {}), ...(formValues || {}) }),
    [formData?.data?.mainrecord, formValues]
  );

  const definitions = useMemo(
    () =>
      items.filter(Boolean).map(item => {
        const { action, type, requiresChecker } = resolveButtonAuthorization(item);
        const verifyUrl = resolveButtonVerifyUrl(getVerifyTemplate(item), values);

        return {
          action,
          type,
          item,
          requiresChecker,
          requiresTransaction: Boolean(moduleName) && !UI_ONLY_ACTIONS.has(action),
          signature: accessSignature({
            moduleName,
            action,
            type,
            verifyUrl,
            checkerUser,
            formType
          }),
          verifyUrl
        };
      }),
    [items, moduleName, checkerUser, formType, values]
  );

  const requestSignature = useMemo(
    () => JSON.stringify(definitions.map(definition => definition.signature)) + `::${token || ""}`,
    [definitions, token]
  );
  const definitionsRef = useRef(definitions);
  definitionsRef.current = definitions;
  const [accessState, setAccessState] = useState({ requestSignature: "", permissions: {} });

  useEffect(() => {
    let active = true;
    const requestCache = new Map();
    setAccessState({ requestSignature, permissions: {} });

    const authenticatedGet = url =>
      axios.get(url, { headers: { Authorization: `Bearer ${token}` } });

    const cachedRequest = (key, request) => {
      if (!requestCache.has(key)) requestCache.set(key, request());
      return requestCache.get(key);
    };

    Promise.all(
      definitionsRef.current.map(async definition => {
        const {
          action,
          requiresChecker,
          requiresTransaction,
          signature,
          verifyUrl
        } = definition;

        if (!action) return [signature, !requiresChecker];
        if (UI_ONLY_ACTIONS.has(action)) return [signature, true];

        try {
          if (!requiresTransaction) return [signature, false];
          const transactionAllowed = await cachedRequest(`transaction:${moduleName}:${action}`, () =>
            authenticatedGet(
              `${serverAddress}/form/isallowed/transaction?module=${encodeURIComponent(moduleName)}` +
                `&activity=${encodeURIComponent(action)}`
            ).then(responseAllowsAccess)
          );
          if (!transactionAllowed) return [signature, false];

          if (verifyUrl) {
            // An unresolved placeholder means the workflow decision cannot yet
            // be tied to the current record, so remain disabled.
            if (/\[[^\]]+]|\{[^{}]+}/.test(verifyUrl)) return [signature, false];
            const resolvedVerifyUrl = /^https?:\/\//i.test(verifyUrl)
              ? verifyUrl
              : `${serverAddress}${verifyUrl.startsWith("/") ? "" : "/"}${verifyUrl}`;
            const workflowAllowed = await cachedRequest(`workflow:${resolvedVerifyUrl}`, () =>
              authenticatedGet(resolvedVerifyUrl).then(responseAllowsAccess)
            );
            if (!workflowAllowed) return [signature, false];
          }

          if (requiresChecker) {
            if (!checkerUser || !formType) return [signature, false];
            const checkerAllowed = await cachedRequest(`checker:${checkerUser}:${formType}`, () =>
              authenticatedGet(
                `${serverAddress}/transaction/ischecker/${encodeURIComponent(checkerUser)}/` +
                  encodeURIComponent(formType)
              ).then(response => {
                const checker = response?.data?.data?.Checker;
                return checker === true || String(checker || "").trim().toUpperCase() === "YES";
              })
            );
            if (!checkerAllowed) return [signature, false];
          }

          return [signature, true];
        } catch (error) {
          console.error(`Unable to verify ${moduleName}/${action} access`, error);
          return [signature, false];
        }
      })
    ).then(results => {
      if (!active) return;
      setAccessState({
        requestSignature,
        permissions: Object.fromEntries(results)
      });
    });

    return () => {
      active = false;
    };
  }, [requestSignature]);

  const isCurrent = accessState.requestSignature === requestSignature;
  const isAllowed = item => {
    const definition = definitions.find(candidate => candidate.item === item);
    if (!definition) return false;
    if (!definition.action) return !definition.requiresChecker;
    if (UI_ONLY_ACTIONS.has(definition.action)) return true;
    return isCurrent && accessState.permissions[definition.signature] === true;
  };

  const isLoading = definitions.some(definition => {
    if (!definition.action || UI_ONLY_ACTIONS.has(definition.action)) return false;
    return !isCurrent || accessState.permissions[definition.signature] === undefined;
  });

  return { isAllowed, isLoading };
}
